import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { FilePenLine, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';

import {
  useCompletePortalConsultantContract,
  useInitiatePortalConsultantContract,
  usePortalConsultantContract,
} from '@alamo/api';
import { useAuth } from '@alamo/auth';
import type { ConsultantPortalContractStatus } from '@alamo/types';
import { Button } from '@alamo/ui';
import { extractApiError, getApiErrorCode } from '@alamo/utils';

import { SignatureEmbed } from '../../pages/Public/MagicLink/components/SignatureEmbed';
import {
  isZapsignSigningUrl,
  resolveSignatureProvider,
} from '../../pages/Public/MagicLink/components/signatureProvider';

import { LogoSvg } from './LogoSvg';

export function isContractCorrectionPath(pathname: string): boolean {
  return ['/my-registration', '/migrate', '/profile', '/consents', '/privacy', '/support'].some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

export interface ContractReconfirmationAccess {
  allowed: boolean;
  contractBlocked: boolean;
  refreshContractStatus: () => Promise<boolean>;
}

function contractStep(status: ConsultantPortalContractStatus): string {
  return [
    status.required,
    status.signed,
    status.isBlocked,
    status.needsCompletion,
    status.nextStep,
  ].join(':');
}

const exemptReconfirmationAccess: ContractReconfirmationAccess = {
  allowed: true,
  contractBlocked: false,
  refreshContractStatus: async () => true,
};

/** The iframe requests reconciliation; only the authenticated server state unlocks the CRM. */
export function ConsultantContractGate({
  children,
}: {
  children: (contractPending: boolean, reconfirmation: ContractReconfirmationAccess) => ReactNode;
}) {
  const { user, consultantType } = useAuth();
  if (!user) return null;
  if (consultantType !== 'pf' && consultantType !== 'pj')
    return children(false, exemptReconfirmationAccess);

  return (
    <ConsultantContractVerification key={`${user.id}:${consultantType}`} userId={user.id}>
      {children}
    </ConsultantContractVerification>
  );
}

function ConsultantContractVerification({
  children,
  userId,
}: {
  children: (contractPending: boolean, reconfirmation: ContractReconfirmationAccess) => ReactNode;
  userId: string;
}) {
  const { t } = useTranslation('auth');
  const { logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const query = usePortalConsultantContract(userId);
  const queryClient = useQueryClient();
  const previousStep = useRef<string>();
  const sessionRefreshGeneration = useRef(0);
  const [sessionSettledStep, setSessionSettledStep] = useState<string>();
  const [isSessionRefreshing, setIsSessionRefreshing] = useState(false);
  const initiate = useInitiatePortalConsultantContract(userId);
  const { mutateAsync: completeContract } = useCompletePortalConsultantContract();
  const [verifiedDuringMount, setVerifiedDuringMount] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const completionInFlight = useRef(false);
  const [completionError, setCompletionError] = useState(false);
  const [embedError, setEmbedError] = useState(false);
  const [sessionRefreshError, setSessionRefreshError] = useState(false);
  const active = useRef(true);
  const { refetch } = query;
  const errorStatus = extractApiError(query.error).status;
  const accessError =
    query.isError &&
    (errorStatus === 401 ||
      errorStatus === 403 ||
      getApiErrorCode(query.error) === 'CONSULTANT_CONTRACT_REQUIRED');
  const freshStatus = query.isFetchedAfterMount && query.isSuccess;

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  // Cached data alone never establishes access for a new identity or layout mount.
  useEffect(() => {
    if (accessError) setVerifiedDuringMount(false);
    else if (freshStatus) setVerifiedDuringMount(true);
  }, [accessError, freshStatus]);

  const completeAccess = useCallback(async () => {
    if (completionInFlight.current) return;
    completionInFlight.current = true;
    setIsCompleting(true);
    sessionRefreshGeneration.current++;
    setIsSessionRefreshing(false);
    setCompletionError(false);
    try {
      await completeContract();
      if (!active.current) return;
      await refreshUser();
      if (!active.current) return;
      const result = await refetch();
      if (!active.current) return;
      if (result.error || !result.data || result.data.isBlocked) {
        setCompletionError(true);
      } else {
        const refreshedStep = contractStep(result.data);
        previousStep.current = refreshedStep;
        setSessionSettledStep(refreshedStep);
        setSessionRefreshError(false);
        void queryClient.invalidateQueries({ queryKey: ['profile'] });
      }
    } catch {
      if (active.current) setCompletionError(true);
    } finally {
      completionInFlight.current = false;
      if (active.current) setIsCompleting(false);
    }
  }, [completeContract, queryClient, refreshUser, refetch]);

  const status = !accessError && (freshStatus || verifiedDuringMount) ? query.data : undefined;
  const contract = status?.contract;
  useEffect(() => {
    setEmbedError(false);
  }, [contract?.contractId, contract?.requestSignatureKey, contract?.providerEnvelopeUrl]);
  const authorized =
    status?.isBlocked === false && status.needsCompletion === false && status.nextStep === null;
  const refreshContractStatus = useCallback(async () => {
    const result = await refetch();
    if (!active.current) return false;
    if (result.isSuccess && result.data.nextStep === 'registration') {
      navigate('/my-registration', { replace: true });
    }
    return (
      result.isSuccess &&
      result.data.isBlocked === false &&
      result.data.needsCompletion === false &&
      result.data.nextStep === null
    );
  }, [navigate, refetch]);
  const step = status ? contractStep(status) : undefined;

  useEffect(() => {
    if (!step || !query.isSuccess || isCompleting) return;
    // The first status follows the freshly authenticated session; later steps must refresh it.
    if (!previousStep.current) {
      previousStep.current = step;
      setSessionSettledStep(step);
      return;
    }
    if (previousStep.current === step) return;
    previousStep.current = step;
    const generation = ++sessionRefreshGeneration.current;
    setSessionRefreshError(false);
    setIsSessionRefreshing(true);
    void refreshUser()
      .then(() => {
        if (active.current && generation === sessionRefreshGeneration.current) {
          setSessionSettledStep(step);
        }
      })
      .catch(() => {
        if (active.current && generation === sessionRefreshGeneration.current) {
          setSessionRefreshError(true);
        }
      })
      .finally(() => {
        if (active.current && generation === sessionRefreshGeneration.current) {
          setIsSessionRefreshing(false);
        }
      });
    void queryClient.invalidateQueries({ queryKey: ['profile'] });
  }, [isCompleting, query.isSuccess, queryClient, refreshUser, step]);

  const retryStatus = useCallback(async () => {
    const generation = ++sessionRefreshGeneration.current;
    setIsSessionRefreshing(true);
    try {
      await refreshUser();
      if (!active.current || generation !== sessionRefreshGeneration.current) return;
      const result = await refetch();
      if (!active.current || generation !== sessionRefreshGeneration.current) return;
      if (!result.isSuccess || !result.data) throw new Error('Contract status unavailable');
      const refreshedStep = contractStep(result.data);
      previousStep.current = refreshedStep;
      setSessionSettledStep(refreshedStep);
      setSessionRefreshError(false);
    } catch {
      if (active.current && generation === sessionRefreshGeneration.current)
        setSessionRefreshError(true);
    } finally {
      if (active.current && generation === sessionRefreshGeneration.current)
        setIsSessionRefreshing(false);
    }
  }, [refetch, refreshUser]);
  const registration = status?.nextStep === 'registration';
  const supportPath = location.pathname === '/support' || location.pathname.startsWith('/support/');
  if (!isCompleting && (authorized || registration || (supportPath && !accessError))) {
    if (supportPath && !authorized && location.pathname === '/support') {
      return <Navigate to="/support/tickets" replace />;
    }
    if (registration && !isContractCorrectionPath(location.pathname)) {
      return <Navigate to="/my-registration" replace />;
    }
    return (
      <>
        {children(!authorized, {
          allowed:
            authorized &&
            query.isSuccess &&
            !accessError &&
            !sessionRefreshError &&
            !isSessionRefreshing &&
            step === sessionSettledStep,
          contractBlocked: Boolean(
            status?.isBlocked || status?.needsCompletion || status?.nextStep,
          ),
          refreshContractStatus,
        })}
        {(query.isFetching || query.isError || sessionRefreshError || isSessionRefreshing) && (
          <div
            data-testid="portal-contract-status-feedback"
            role={query.isError || sessionRefreshError ? 'alert' : 'status'}
            className="fixed bottom-4 left-4 right-4 z-50 ml-auto flex max-w-lg flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-alamo-surface p-4 font-body text-sm text-slate-600 shadow-lg"
          >
            {(query.isFetching || isSessionRefreshing) && (
              <Loader2
                size={18}
                className="shrink-0 animate-spin text-azul-protecao"
                aria-hidden="true"
              />
            )}
            <p className="min-w-0 flex-1">
              {t(
                query.isError || sessionRefreshError
                  ? 'portal_contract.background_error'
                  : 'portal_contract.checking',
              )}
            </p>
            {(query.isError || sessionRefreshError) && (
              <Button
                size="sm"
                isLoading={query.isFetching || isSessionRefreshing}
                onClick={() => void retryStatus()}
              >
                {t('portal_contract.retry')}
              </Button>
            )}
          </div>
        )}
      </>
    );
  }

  const loading = !status && !query.isError;
  const completing = isCompleting;
  const completion = status?.signed && status.needsCompletion;
  const signature = status?.nextStep === 'signature' && status.required && !status.signed;
  const statusError =
    (!status && query.isError) || (!loading && !completing && !completion && !signature);
  const transientStatusError = Boolean(status) && query.isError && !accessError;
  const hasEmbed =
    resolveSignatureProvider({
      provider: contract?.provider,
      envelopeUrl: contract?.providerEnvelopeUrl,
    }) === 'zapsign'
      ? isZapsignSigningUrl(contract?.providerEnvelopeUrl)
      : Boolean(contract?.requestSignatureKey);
  const signingError =
    embedError || Boolean(contract && !hasEmbed) || (initiate.isError && !hasEmbed);

  return (
    <div className="min-h-screen bg-alamo-bg" data-testid="portal-contract-gate">
      <header className="flex items-center justify-between gap-4 bg-azul-protecao px-5 py-4 md:px-8">
        <LogoSvg className="h-8 w-auto md:h-10" />
        <Link
          to="/support/tickets"
          className="ml-auto inline-flex min-h-[44px] items-center rounded-lg px-3 font-body text-sm text-white hover:bg-white/10"
        >
          {t('portal_contract.support')}
        </Link>
        <Button
          variant="ghost"
          className="text-white hover:bg-white/10 hover:text-white"
          onClick={() => void logout()}
        >
          <LogOut size={16} aria-hidden="true" />
          {t('portal_contract.logout')}
        </Button>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 px-4 py-8 md:px-8 md:py-12">
        <div className="flex items-center gap-2 font-body text-sm text-azul-protecao">
          <ShieldCheck size={18} aria-hidden="true" />
          <span>{t('portal_contract.authenticated')}</span>
        </div>
        <div className="max-w-2xl space-y-3">
          <h1 className="font-heading text-2xl font-semibold leading-tight text-ink md:text-3xl">
            {t(
              loading
                ? 'portal_contract.loading'
                : completing
                  ? 'portal_contract.completing'
                  : statusError
                    ? 'portal_contract.status_error_title'
                    : completion
                      ? 'portal_contract.completion_title'
                      : 'portal_contract.title',
            )}
          </h1>
          <p className="font-body text-base leading-relaxed text-slate-600">
            {t(
              loading
                ? 'portal_contract.loading_description'
                : completing
                  ? 'portal_contract.completion_description'
                  : statusError
                    ? 'portal_contract.status_error_description'
                    : completion
                      ? 'portal_contract.completion_description'
                      : 'portal_contract.description',
            )}
          </p>
        </div>
        <section
          className="space-y-5 rounded-2xl border border-slate-200 bg-alamo-surface p-5 md:p-8"
          style={{ boxShadow: 'var(--elev-1)' }}
        >
          {transientStatusError && (
            <div
              role="alert"
              data-testid="portal-contract-signature-status-feedback"
              className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-alamo-bg p-3 font-body text-sm text-slate-600"
            >
              <p className="min-w-0 flex-1">{t('portal_contract.signature_status_error')}</p>
              <Button
                variant="secondary"
                isLoading={query.isFetching}
                onClick={() => void refetch()}
              >
                {t('portal_contract.retry')}
              </Button>
            </div>
          )}
          {loading || completing ? (
            <div
              role="status"
              className="flex items-center gap-3 py-8 font-body text-sm text-slate-600"
            >
              <Loader2 className="animate-spin text-azul-protecao" size={22} aria-hidden="true" />
              {t(completing ? 'portal_contract.confirming' : 'portal_contract.checking')}
            </div>
          ) : statusError ? (
            <>
              <p role="alert" className="font-body text-sm text-erro-apoio">
                {t('portal_contract.status_error')}
              </p>
              <Button isLoading={query.isFetching} onClick={() => void refetch()}>
                {t('portal_contract.retry')}
              </Button>
            </>
          ) : completion ? (
            <>
              {completionError && (
                <p role="alert" className="font-body text-sm text-erro-apoio">
                  {t('portal_contract.completion_error')}
                </p>
              )}
              <Button onClick={() => void completeAccess()}>
                {t(completionError ? 'portal_contract.retry' : 'portal_contract.complete')}
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-start gap-3 rounded-xl bg-laranja-encanto-04 p-4">
                <FilePenLine
                  className="mt-0.5 shrink-0 text-laranja-encanto"
                  size={22}
                  aria-hidden="true"
                />
                <div className="space-y-1 font-body text-sm">
                  <p className="font-semibold text-ink">{t('portal_contract.required')}</p>
                  <p className="leading-relaxed text-slate-600">
                    {t('portal_contract.requirement_hint')}
                  </p>
                </div>
              </div>
              {signingError && (
                <p role="alert" className="font-body text-sm text-erro-apoio">
                  {t('portal_contract.signature_error')}
                </p>
              )}
              {(!contract || signingError) && status?.canInitiate && (
                <Button
                  isLoading={initiate.isPending}
                  onClick={() => {
                    setEmbedError(false);
                    initiate.mutate();
                  }}
                >
                  {t(signingError ? 'portal_contract.retry' : 'portal_contract.open')}
                </Button>
              )}
              {contract && !signingError && !initiate.isPending && (
                <div className="min-h-[520px] overflow-hidden rounded-xl border border-slate-200">
                  <SignatureEmbed
                    provider={contract.provider}
                    requestSignatureKey={contract.requestSignatureKey}
                    envelopeUrl={contract.providerEnvelopeUrl}
                    onSigned={() => void refetch()}
                    onError={() => setEmbedError(true)}
                    onRetry={() => initiate.mutate()}
                  />
                </div>
              )}
              <p className="font-body text-xs leading-relaxed text-slate-500">
                {t('portal_contract.return_hint')}
              </p>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
